
import { RouteLocationNormalized, useRoute, useRouter } from 'vue-router'

import DateSelector from '@/components/DateSelector.vue'
import ErrorMessage from '@/components/ErrorMessage.vue'
import Leaderboard from '@/components/Leaderboard.vue'
import * as wikipedia from '@/wikipedia'
import { Article } from '@/wikipedia'
import * as DateFns from 'date-fns'
import { RemoteData } from '@/remoteData'
import { defineComponent, onMounted, ref, Ref, watch } from 'vue'

export default defineComponent({
  components: {
    DateSelector,
    ErrorMessage,
    Leaderboard,
  },
  setup() {
    const date: Ref<Date | null> = ref(null)
    const maxDate: Ref<Date> = ref(DateFns.startOfToday())
    const cache: Ref<Cache | null> = ref(null)
    const articles: Ref<RemoteData<Article[]>> = ref({
      status: 'loading',
    } as const)

    const route = useRoute()
    const router = useRouter()

    const onRouteChange = async (val: RouteLocationNormalized) => {
      const newDate = parseDateFromRoute(val, maxDate.value)
      if (!newDate) {
        rerouteToMaxDate()
        return
      }
      await updateDate(newDate)
    }

    const parseDateFromRoute = (
      route: RouteLocationNormalized,
      maxDate: Date | null
    ): Date | null => {
      if (route.fullPath === '/') {
        return maxDate
      } else {
        const newDate = DateFns.startOfDay(
          new Date(route.params.date as string)
        )
        if (
          isNaN(newDate.getDate()) ||
          (maxDate && DateFns.isAfter(newDate, maxDate))
        ) {
          return null
        } else {
          return newDate
        }
      }
    }
    const rerouteToMaxDate = () => {
      const formattedDate = DateFns.format(maxDate.value, 'yyyy-MM-dd')
      router.replace({
        name: 'exact-date',
        params: { date: formattedDate },
      })
    }

    const updateDate = async (newDate: Date) => {
      date.value = newDate
      const setToLoading = setTimeout(() => {
        articles.value = {
          status: 'loading',
        }
      }, 75)

      try {
        const data = await wikipedia.getResultsForDate(newDate, cache.value)
        // to avoid race conditions where the date is changed before the request
        // for the previous date is resolved
        if (date.value === newDate) {
          clearTimeout(setToLoading)
          articles.value = {
            data,
            status: 'okay',
          }
        }
      } catch (e) {
        clearTimeout(setToLoading)
        articles.value = { status: 'error' }
      }
    }

    watch(
      () => [route.fullPath, route.params],
      async () => {
        if (router.resolve({ name: 'talk' }).href === route.fullPath) {
          return
        }
        await onRouteChange(route)
      }
    )

    onMounted(async () => {
      date.value = parseDateFromRoute(route, null)
      cache.value =
        window.caches && (await window.caches.open('wikipedia-analytics'))
      maxDate.value = await wikipedia.maxDate(cache.value)
      const newDate = parseDateFromRoute(route, maxDate.value)
      if (!newDate) {
        rerouteToMaxDate()
        return
      }
      await updateDate(newDate)
    })

    return {
      date,
      maxDate,
      articles,
    }
  },
})
